import { Map, List } from 'immutable'
import { createSelector } from 'reselect'
import { getActiveUserId } from 'data/ui/selectors'
import {
  getCurrentNoteThreadIds,
  getCurrentNoteClosedThreadIds,
} from 'data/threads/selectors'
import { getEntity, getEntityMap, getListState } from 'data'
import partition from 'immutable-partition'
export const ALL_NOTIFICATIONS_LIST_NAME = 'allNotifications'

export const getNotifications = state => getEntityMap(state, 'Notification')
export const getNotification = (state, id) =>
  getEntity(state, 'Notification', id)

export const getUnreadNotifications = createSelector(
  getNotifications,
  notifications => notifications.filter(({ aknowledgedAt }) => !aknowledgedAt)
)

export const getCurrentUserNotifications = state =>
  getEntity(state, 'UserNotifications', getActiveUserId(state))

export const getUnreadNotificationsCount = createSelector(
  getCurrentUserNotifications,
  userNotifications => {
    return userNotifications
      ? userNotifications.get('unreadNotificationsCount')
      : 0
  }
)

export const getAllNotificationsListState = getListState(
  ALL_NOTIFICATIONS_LIST_NAME
)

export const getNotificationFromIndex = (state, index) => {
  const notificationId = getAllNotificationIds(state)[index]
  if (notificationId) {
    return getNotification(state, notificationId)
  }
}

export const getAllNotificationIds = state =>
  getAllNotificationsListState(state).nodes

function partitionNotificationReadness(notification) {
  return notification.aknowledgedAt === null ? 'unread' : 'read'
}

function notificationSorterByCreationDate(a, b) {
  return new Date(b.createdAt) - new Date(a.createdAt)
}

export const getLastNotificationIds = (state, count) => {
  const notifications = partition(
    partitionNotificationReadness,
    getNotifications(state)
  )

  const read = notifications.get('read') || new List()
  const unread = notifications.get('unread') || new List()

  return unread
    .sort(notificationSorterByCreationDate)
    .concat(read.sort(notificationSorterByCreationDate))
    .map(({ id }) => id)
    .slice(0, count)
    .toJS()
}

export const getCommentNotifications = createSelector(
  getNotifications,
  notifications => commentId => {
    return notifications.filter(
      ({ aknowledgedAt, metadata }) =>
        !aknowledgedAt && metadata && metadata.get('commentId') === commentId
    )
  }
)

export const getThreadNotifications = createSelector(
  getNotifications,
  notifications => threadId => {
    return notifications.filter(
      ({ aknowledgedAt, metadata }) =>
        !aknowledgedAt && metadata && metadata.get('threadId') === threadId
    )
  }
)

export const getThreadsNotifications = createSelector(
  getNotifications,
  notifications => threadsIds => {
    return threadsIds.reduce((notificationsByThread, threadId) => {
      const threadNotifications = notifications.filter(
        ({ aknowledgedAt, metadata }) =>
          !aknowledgedAt && metadata && metadata.get('threadId') === threadId
      )
      return notificationsByThread.set(threadId, threadNotifications)
    }, Map())
  }
)

export const getCurrentNoteThreadsNotificationsCount = createSelector(
  getThreadsNotifications,
  getCurrentNoteThreadIds,
  (getNotificationsForThreads, threadIds) => {
    const notifications = getNotificationsForThreads(threadIds)
    return threadIds.reduce((count, threadId) => {
      const threadNotifications = notifications.get(threadId)
      return threadNotifications ? count + threadNotifications.count() : count
    }, 0)
  }
)

export const getCurrentNoteThreadsNotificationsActivity = createSelector(
  getThreadsNotifications,
  getCurrentNoteThreadIds,
  getCurrentNoteClosedThreadIds,
  (getNotificationsForThreads, threadIds, closedThreadIds) => {
    const notifications = getNotificationsForThreads(threadIds)
    return threadIds.reduce(
      ({ openThreadsActive, closedThreadsActive }, threadId) => {
        const threadNotifications = notifications.get(threadId)
        if (threadNotifications.size) {
          return closedThreadIds.includes(threadId)
            ? {
                openThreadsActive,
                closedThreadsActive: true,
              }
            : {
                openThreadsActive: true,
                closedThreadsActive,
              }
        }
        return { closedThreadsActive, openThreadsActive }
      },
      { openThreadsActive: false, closedThreadsActive: false }
    )
  }
)
